/**
 * useAuthorizationData Hook
 * Manages authorization data fetching, refreshing, and state
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { AuthorizationsService, Authorization } from '../../../../services/patientmr/authorizations.service';

export interface UseAuthorizationDataOptions {
  patientId?: string;
  expanded: boolean;
  onError?: (error: string) => void;
}

export interface UseAuthorizationDataResult {
  authorizations: Authorization[];
  loading: boolean;
  error: string | null;
  hasLoadedOnce: boolean;
  refreshing: boolean;
  refreshAuthorizations: () => Promise<void>;
  setError: (error: string | null) => void;
}

export function useAuthorizationData({
  patientId,
  expanded,
  onError
}: UseAuthorizationDataOptions): UseAuthorizationDataResult {
  const [authorizations, setAuthorizations] = useState<Authorization[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Ref for deduplication
  const hasLoadedAuthorizationsRef = useRef<string | null>(null);

  // Fetch authorizations from API
  const fetchAuthorizations = useCallback(async (isRefresh = false) => {
    if (!patientId) {
      console.warn('No patientId provided to authorizations component');
      const errorMsg = 'Patient information not available. Please select a patient to view authorizations.';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }

    // Skip if already loaded for this patient or if currently loading (unless refreshing)
    if (!isRefresh && (hasLoadedAuthorizationsRef.current === patientId || loading)) {
      return;
    }

    hasLoadedAuthorizationsRef.current = patientId;

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      console.log(`${isRefresh ? 'Refreshing' : 'Fetching'} authorizations for patient ${patientId}`);
      const authorizationData = await AuthorizationsService.getAuthorizations(patientId);
      console.log(`Loaded ${authorizationData.length} authorizations for patient ${patientId}`);

      setAuthorizations(authorizationData);
      setHasLoadedOnce(true);
    } catch (err: any) {
      console.error('Error fetching authorizations:', err);

      let errorMsg = 'Failed to load authorizations. Please try again.';
      if (err.message && err.message.includes('Authentication token not found')) {
        errorMsg = 'Authentication error. Please log in again.';
      } else if (err.message && err.message.includes('Failed to fetch')) {
        errorMsg = 'Server error. Please try again later.';
      }

      setError(errorMsg);
      if (onError) onError(errorMsg);

      // Reset ref on error to allow retry
      hasLoadedAuthorizationsRef.current = null;
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loading is only a re-entry guard; adding it would re-run the load when loading toggles
  }, [patientId, onError]);

  // Manual refresh function
  const refreshAuthorizations = useCallback(async () => {
    if (!patientId || refreshing) {
      if (!patientId) {
        const errorMsg = 'No patient selected. Please select a patient to view authorizations.';
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
      return;
    }

    // Reset ref to allow reload
    hasLoadedAuthorizationsRef.current = null;
    await fetchAuthorizations(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loading is only a re-entry guard; adding it would re-run the load when loading toggles
  }, [patientId, fetchAuthorizations, onError]);

  // Load authorizations when the tab is expanded
  useEffect(() => {
    if (expanded && patientId) {
      fetchAuthorizations();
    }

    // Reset state when collapsed
    if (!expanded) {
      setError(null);
      setHasLoadedOnce(false);
      hasLoadedAuthorizationsRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAuthorizations changes identity when its guards change; the load must run only on patient/expand change
  }, [patientId, expanded]);

  return {
    authorizations,
    loading,
    error,
    hasLoadedOnce,
    refreshing,
    refreshAuthorizations,
    setError
  };
}
